/**
 * Performance optimization utilities to prevent forced reflows
 * and improve rendering performance
 */

export class PerformanceHelper {
    private static instance: PerformanceHelper;
    private readQueue: Array<() => any> = [];
    private writeQueue: Array<() => void> = [];
    private isScheduled = false;

    static getInstance(): PerformanceHelper {
        if (!PerformanceHelper.instance) {
            PerformanceHelper.instance = new PerformanceHelper();
        }
        return PerformanceHelper.instance;
    }

    /**
     * Batches DOM reads and writes to prevent forced reflows
     * @param reads Array of functions that read from DOM
     * @param writes Array of functions that write to DOM
     */
    public batchDOMOperations(reads: Array<() => any>, writes: Array<() => void>) {
        return new Promise<any[]>(resolve => {
            // Schedule all reads first
            const readResults: any[] = [];
            reads.forEach(read => {
                this.readQueue.push(() => {
                    readResults.push(read());
                });
            });

            // Then schedule all writes
            writes.forEach(write => {
                this.writeQueue.push(write);
            });

            this.scheduleFlush(() => resolve(readResults));
        });
    }

    /**
     * Queues a DOM read operation to be batched with other reads
     * This prevents forced reflows by batching all reads before writes
     */
    public queueDOMRead(callback: () => any): Promise<any> {
        return new Promise(resolve => {
            this.readQueue.push(() => {
                resolve(callback());
            });
            this.scheduleFlush();
        });
    }

    /**
     * Queues a DOM write operation to be batched with other writes
     * This prevents forced reflows by executing all writes after reads
     */
    public queueDOMWrite(callback: () => void): Promise<void> {
        return new Promise(resolve => {
            this.writeQueue.push(() => {
                callback();
                resolve();
            });
            this.scheduleFlush();
        });
    }

    /**
     * Creates a debounced version of a function
     */
    public debounce<T extends (...args: any[]) => any>(func: T, wait: number): T {
        let timeout: NodeJS.Timeout;
        return ((...args: any[]) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), wait);
        }) as T;
    }

    /**
     * Creates a throttled version of a function
     */
    public throttle<T extends (...args: any[]) => any>(func: T, limit: number): T {
        let inThrottle: boolean;
        return ((...args: any[]) => {
            if (!inThrottle) {
                func.apply(this, args);
                inThrottle = true;
                setTimeout(() => (inThrottle = false), limit);
            }
        }) as T;
    }

    /**
     * Optimized scroll handler that uses requestAnimationFrame
     */
    public onScroll(callback: (event: Event) => void): (event: Event) => void {
        let isScrolling = false;

        return (event: Event) => {
            if (!isScrolling) {
                requestAnimationFrame(() => {
                    callback(event);
                    isScrolling = false;
                });
                isScrolling = true;
            }
        };
    }

    /**
     * Optimized resize handler with debouncing
     */
    public onResize(callback: (event: Event) => void, delay: number = 150): (event: Event) => void {
        return this.debounce(callback, delay);
    }

    /**
     * Add passive event listeners for touch and scroll events
     * Automatically throttles for better performance
     */
    public addPassiveScrollListener(
        element: Element | Window | Document,
        callback: (event: Event) => void,
        options: {throttle?: boolean; includeTouch?: boolean} = {}
    ): () => void {
        const {throttle = true, includeTouch = false} = options;

        let isScrolling = false;
        const throttledCallback = throttle
            ? (event: Event) => {
                  if (!isScrolling) {
                      requestAnimationFrame(() => {
                          callback(event);
                          isScrolling = false;
                      });
                      isScrolling = true;
                  }
              }
            : callback;

        // Add scroll listener
        element.addEventListener('scroll', throttledCallback, {passive: true});

        // Add touch listener if requested
        if (includeTouch) {
            element.addEventListener('touchmove', throttledCallback, {passive: true});
        }

        // Return cleanup function
        return () => {
            element.removeEventListener('scroll', throttledCallback);
            if (includeTouch) {
                element.removeEventListener('touchmove', throttledCallback);
            }
        };
    }

    /**
     * Safely measure element dimensions without causing reflows
     */
    public measureElement(
        element: Element
    ): Promise<{width: number; height: number; rect: DOMRect}> {
        return this.queueDOMRead(() => ({
            width: element.clientWidth,
            height: element.clientHeight,
            rect: element.getBoundingClientRect(),
        }));
    }

    /**
     * Schedule a flush of the read/write queues
     */
    private scheduleFlush(callback?: () => void) {
        if (!this.isScheduled) {
            this.isScheduled = true;
            requestAnimationFrame(() => {
                this.flush();
                if (callback) callback();
            });
        }
    }

    /**
     * Flush all queued operations
     */
    private flush() {
        // Execute all reads first
        while (this.readQueue.length > 0) {
            const read = this.readQueue.shift();
            if (read) read();
        }

        // Then execute all writes
        while (this.writeQueue.length > 0) {
            const write = this.writeQueue.shift();
            if (write) write();
        }

        this.isScheduled = false;
    }
}

export default PerformanceHelper;
